import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Plus,
  Trash2,
  Image as ImageIcon,
  Eye,
  EyeOff,
  X,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SaveBar } from '@/components/ui/save-bar';
import { toast } from '@/components/ui/sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/cn';
import { useBrief, useUpdateBriefSlice, useMe } from '@/lib/brief';
import { RichTextEditor } from '@/components/rich-text/editor';
import { MediaPicker } from '@/components/media/picker';
import { useMediaHost, type MediaAsset } from '@/lib/media';

export const Route = createFileRoute('/_app/tresci/realizacje')({
  staticData: { crumb: 'Realizacje' },
  component: RealizacjePage,
});

interface ImagePair {
  caption?: string;
  before_url: string;
  after_url: string;
  before_alt?: string;
  after_alt?: string;
}

interface CaseStudy {
  slug: string;
  title: string;
  excerpt?: string;
  procedure_slug?: string;
  patient?: string;
  treated_on?: string;
  performed_by?: string;
  body_html: string;
  image_pairs: ImagePair[];
  cover_url?: string;
  cover_alt?: string;
  consent_signed_at?: string;
  tags?: string[];
  category?: string;
  published_at?: string;
  published?: boolean;
  created_at?: number;
  updated_at?: number;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l').replace(/Ł/g, 'l')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function RealizacjePage() {
  const { data: brief } = useBrief();
  const me = useMe();
  const update = useUpdateBriefSlice();
  const items = ((brief?.case_studies ?? []) as CaseStudy[]) || [];
  const team = ((brief?.team ?? []) as Array<{ id: string; full_name: string }>) || [];

  const [activeSlug, setActiveSlug] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<CaseStudy | null>(null);
  const [dirty, setDirty] = React.useState(false);
  const [saveStatus, setSaveStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');
  const [confirmDelete, setConfirmDelete] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!activeSlug) { setDraft(null); setDirty(false); return; }
    const c = items.find((x) => x.slug === activeSlug);
    if (c) {
      setDraft({ ...c, image_pairs: [...(c.image_pairs ?? [])] });
      setDirty(false);
    }
  }, [activeSlug, items.length]);

  const newCaseStudy = () => {
    const now = Math.floor(Date.now() / 1000);
    const base: CaseStudy = {
      slug: `realizacja-${now.toString(36)}`,
      title: 'Nowa realizacja',
      body_html: '<p>Opisz przebieg leczenia, podejście zespołu i efekt końcowy…</p>',
      image_pairs: [],
      published: false,
      published_at: new Date().toISOString().slice(0, 10),
      tags: [],
      created_at: now,
      updated_at: now,
    };
    setActiveSlug(base.slug);
    setDraft(base);
    setDirty(true);
  };

  const saveDraft = async () => {
    if (!draft) return;
    if (!draft.title || draft.title.length < 3) {
      toast.error('Tytuł musi mieć min. 3 znaki.');
      return;
    }
    if (!draft.body_html || draft.body_html.length < 20) {
      toast.error('Opis musi mieć min. 20 znaków.');
      return;
    }
    const cleaned: CaseStudy = {
      ...draft,
      slug: draft.slug || slugify(draft.title),
      updated_at: Math.floor(Date.now() / 1000),
    };
    const others = items.filter((c) => c.slug !== cleaned.slug && c.slug !== activeSlug);
    if (others.some((c) => c.slug === cleaned.slug)) {
      toast.error('Slug już istnieje — wybierz inny.');
      return;
    }
    const next = [...others, cleaned];
    try {
      await update.mutateAsync({ slice: 'case_studies', data: next });
      setActiveSlug(cleaned.slug);
      setDraft(cleaned);
      setDirty(false);
      setSaveStatus('saved');
      toast.success('Zapisano realizację');
    } catch (e) {
      setSaveStatus('error');
      toast.error('Nie udało się zapisać: ' + (e as Error).message);
    }
  };

  const togglePublish = async (cs: CaseStudy) => {
    if (!cs.consent_signed_at) {
      toast.error('Najpierw oznacz zgodę pacjenta (RODO) — bez niej realizacja nie wyświetli się publicznie.');
      return;
    }
    const next = items.map((c) => (c.slug === cs.slug ? { ...c, published: !c.published } : c));
    try {
      await update.mutateAsync({ slice: 'case_studies', data: next });
      toast.success(cs.published ? 'Ukryto' : 'Opublikowano');
    } catch (e) {
      toast.error('Nie udało się zmienić statusu: ' + (e as Error).message);
    }
  };

  const removeItem = async (slug: string) => {
    const next = items.filter((c) => c.slug !== slug);
    try {
      await update.mutateAsync({ slice: 'case_studies', data: next });
      if (activeSlug === slug) setActiveSlug(null);
      toast.success('Realizacja usunięta');
    } catch (e) {
      toast.error('Nie udało się usunąć: ' + (e as Error).message);
    } finally {
      setConfirmDelete(null);
    }
  };

  const tenantSlug = me.data?.tenant.slug;

  return (
    <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
      <aside className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-text-subtle">
            {items.length} {items.length === 1 ? 'realizacja' : 'realizacji'}
          </h2>
          <Button type="button" size="sm" onClick={newCaseStudy}>
            <Plus className="size-4" aria-hidden="true" /> Nowa
          </Button>
        </div>
        {items.length === 0 ? (
          <Card>
            <CardContent className="py-6">
              <p className="text-sm text-text-muted">
                Brak realizacji. Kliknij <strong>Nowa</strong>, aby dodać pierwszą.
              </p>
            </CardContent>
          </Card>
        ) : (
          <ul role="list" className="space-y-1.5">
            {items
              .slice()
              .sort((a, b) => (b.updated_at ?? 0) - (a.updated_at ?? 0))
              .map((c) => {
                const active = c.slug === activeSlug;
                const hasConsent = !!c.consent_signed_at;
                return (
                  <li key={c.slug}>
                    <button
                      type="button"
                      onClick={() => setActiveSlug(c.slug)}
                      className={cn(
                        'flex w-full items-start gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                        active
                          ? 'border-accent/30 bg-accent/10'
                          : 'border-border bg-surface hover:bg-bg',
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-text">{c.title}</span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-text-subtle">
                          <code className="font-mono">/realizacje/{c.slug}</code>
                        </span>
                      </span>
                      <span className="flex shrink-0 flex-col items-end gap-1">
                        {c.published === false ? (
                          <Badge variant="soft">szkic</Badge>
                        ) : (
                          <Badge variant="success">live</Badge>
                        )}
                        {!hasConsent ? (
                          <Badge variant="destructive" title="Brak zgody RODO">RODO</Badge>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
          </ul>
        )}
      </aside>

      <section className="min-w-0">
        {!draft ? (
          <Card>
            <CardContent className="py-12">
              <p className="text-center text-sm text-text-muted">
                Wybierz realizację po lewej lub kliknij <strong>Nowa</strong>, aby zacząć.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <CardTitle>{draft.title || 'Nowa realizacja'}</CardTitle>
                  <CardDescription>
                    Publiczny adres: <code>https://{tenantSlug ?? 'twoja-klinika'}.pozyskajpacjenta.pl/realizacje/{draft.slug || 'slug'}</code>
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => togglePublish(draft)}
                  >
                    {draft.published === false ? (
                      <><Eye className="size-3.5" aria-hidden="true" /> Opublikuj</>
                    ) : (
                      <><EyeOff className="size-3.5" aria-hidden="true" /> Ukryj</>
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmDelete(draft.slug)}
                  >
                    <Trash2 className="size-3.5" aria-hidden="true" /> Usuń
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <CaseStudyEditor
                value={draft}
                team={team}
                onChange={(next) => {
                  setDraft(next);
                  setDirty(true);
                }}
              />

              <SaveBar
                className="mt-6"
                saving={update.isPending}
                dirty={dirty}
                status={saveStatus}
                onCancel={() => {
                  const orig = items.find((c) => c.slug === activeSlug);
                  if (orig) {
                    setDraft({ ...orig, image_pairs: [...(orig.image_pairs ?? [])] });
                    setDirty(false);
                  } else {
                    setActiveSlug(null);
                  }
                }}
                onSave={saveDraft}
              />
            </CardContent>
          </Card>
        )}
      </section>

      {confirmDelete ? (
        <AlertDialog open={true} onOpenChange={(o) => !o && setConfirmDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Usunąć realizację?</AlertDialogTitle>
              <AlertDialogDescription>
                Realizacja zniknie z publicznej witryny po najbliższej publikacji. Tej operacji nie da się cofnąć.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Anuluj</AlertDialogCancel>
              <AlertDialogAction onClick={() => confirmDelete && removeItem(confirmDelete)}>
                Usuń
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  );
}

function CaseStudyEditor({
  value,
  team,
  onChange,
}: {
  value: CaseStudy;
  team: Array<{ id: string; full_name: string }>;
  onChange: (next: CaseStudy) => void;
}) {
  const mediaHost = useMediaHost();
  const [coverOpen, setCoverOpen] = React.useState(false);
  const [pairPicker, setPairPicker] = React.useState<{ index: number; side: 'before' | 'after' } | null>(null);

  const onPickCover = (asset: MediaAsset) => {
    onChange({
      ...value,
      cover_url: `${mediaHost}${asset.public_url}`,
      cover_alt: asset.alt_text ?? '',
    });
    setCoverOpen(false);
  };

  const onPickPair = (asset: MediaAsset) => {
    if (!pairPicker) return;
    const url = `${mediaHost}${asset.public_url}`;
    const pairs = value.image_pairs.map((p, i) => {
      if (i !== pairPicker.index) return p;
      if (pairPicker.side === 'before') {
        return { ...p, before_url: url, before_alt: asset.alt_text ?? p.before_alt };
      }
      return { ...p, after_url: url, after_alt: asset.alt_text ?? p.after_alt };
    });
    onChange({ ...value, image_pairs: pairs });
    setPairPicker(null);
  };

  const addPair = () => {
    onChange({
      ...value,
      image_pairs: [...value.image_pairs, { before_url: '', after_url: '', caption: '' }],
    });
  };

  const removePair = (i: number) => {
    onChange({ ...value, image_pairs: value.image_pairs.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="space-y-5">
      <Field label="Tytuł" required>
        <Input
          value={value.title}
          onChange={(e) => {
            const title = e.currentTarget.value;
            onChange({
              ...value,
              title,
              slug: value.slug && !value.slug.startsWith('realizacja-') ? value.slug : slugify(title),
            });
          }}
          placeholder={'Np. „Pełna rekonstrukcja zgryzu — Pani Anna"'}
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Slug (URL)" hint="Tylko małe litery, cyfry i myślniki.">
          <Input
            value={value.slug}
            onChange={(e) => onChange({ ...value, slug: slugify(e.currentTarget.value) })}
          />
        </Field>
        <Field label="Powiązany zabieg (opcjonalnie)" hint="Slug usługi z biblioteki — łączy realizację z /zabiegi/<slug>.">
          <Input
            value={value.procedure_slug ?? ''}
            onChange={(e) => onChange({ ...value, procedure_slug: e.currentTarget.value || undefined })}
            placeholder="rekonstrukcja-zgryzu"
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Pacjent / zwierzę (anonimowo)" hint={'Np. „Czarek, mix, 6 lat" — bez danych identyfikujących.'}>
          <Input
            value={value.patient ?? ''}
            onChange={(e) => onChange({ ...value, patient: e.currentTarget.value || undefined })}
          />
        </Field>
        <Field label="Data zabiegu (opcjonalnie)">
          <Input
            value={value.treated_on ?? ''}
            onChange={(e) => onChange({ ...value, treated_on: e.currentTarget.value || undefined })}
            placeholder="maj 2026"
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Wykonał (członek zespołu)">
          <select
            value={value.performed_by ?? ''}
            onChange={(e) => onChange({ ...value, performed_by: e.currentTarget.value || undefined })}
            className="h-9 w-full rounded-md border border-border bg-bg px-3 text-sm"
          >
            <option value="">— wybierz —</option>
            {team.map((t) => (
              <option key={t.id} value={t.id}>{t.full_name}</option>
            ))}
          </select>
        </Field>
        <Field label="Data publikacji">
          <Input
            type="date"
            value={value.published_at ?? ''}
            onChange={(e) => onChange({ ...value, published_at: e.currentTarget.value || undefined })}
          />
        </Field>
      </div>

      <Field label="Zajawka">
        <Textarea
          value={value.excerpt ?? ''}
          onChange={(e) => onChange({ ...value, excerpt: e.currentTarget.value || undefined })}
          rows={2}
          maxLength={280}
          placeholder="1–2 zdania pokazywane na liście realizacji."
        />
      </Field>

      {/* RODO consent gate */}
      <div className={cn(
        'rounded-md p-4 ring-1',
        value.consent_signed_at
          ? 'bg-success/5 ring-success/20'
          : 'bg-warning/5 ring-warning/30',
      )}>
        <div className="flex items-start gap-3">
          {value.consent_signed_at ? (
            <ShieldCheck className="size-5 shrink-0 text-success" aria-hidden="true" />
          ) : (
            <AlertTriangle className="size-5 shrink-0 text-warning" aria-hidden="true" />
          )}
          <div className="flex-1 space-y-2">
            <p className="text-sm font-semibold text-text">
              Zgoda pacjenta na publikację (RODO)
            </p>
            <p className="text-xs text-text-muted">
              Wymagana, aby realizacja pojawiła się publicznie. Brak zgody → wpis nie wyświetli się
              nawet z flagą „opublikuj".
            </p>
            <Input
              type="date"
              value={value.consent_signed_at ?? ''}
              onChange={(e) => onChange({ ...value, consent_signed_at: e.currentTarget.value || undefined })}
              placeholder="YYYY-MM-DD"
              className="max-w-xs"
            />
          </div>
        </div>
      </div>

      <Field label="Opis przebiegu leczenia" hint="Edytor WYSIWYG — formatowanie, listy, linki, obrazy.">
        <RichTextEditor
          value={value.body_html}
          onChange={(html) => onChange({ ...value, body_html: html })}
          placeholder="Opisz problem, diagnostykę, leczenie i efekt…"
          minHeight={320}
        />
      </Field>

      <Field label="Zdjęcie tytułowe (opcjonalnie)" hint={'Pokazywane na liście realizacji. Jeśli puste — pierwsze zdjęcie „po" zostanie użyte automatycznie.'}>
        <div className="flex items-start gap-4">
          {value.cover_url ? (
            <img
              src={value.cover_url}
              alt={value.cover_alt ?? ''}
              className="size-32 rounded-md object-cover ring-1 ring-border"
            />
          ) : (
            <div className="grid size-32 place-items-center rounded-md bg-surface text-text-subtle ring-1 ring-border ring-dashed">
              <ImageIcon className="size-6" aria-hidden="true" />
            </div>
          )}
          <div className="flex flex-1 flex-col gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => setCoverOpen(true)}>
              <ImageIcon className="size-3.5" aria-hidden="true" />
              {value.cover_url ? 'Zmień' : 'Wybierz z biblioteki'}
            </Button>
            {value.cover_url ? (
              <Button type="button" size="sm" variant="ghost" onClick={() => onChange({ ...value, cover_url: undefined, cover_alt: undefined })}>
                <X className="size-3.5" aria-hidden="true" /> Usuń
              </Button>
            ) : null}
          </div>
        </div>
      </Field>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-text">Zdjęcia przed i po</p>
            <p className="text-xs text-text-muted">
              Dodaj pary zdjęć. Renderujemy je w siatce na stronie realizacji.
            </p>
          </div>
          <Button type="button" size="sm" variant="secondary" onClick={addPair}>
            <Plus className="size-3.5" aria-hidden="true" /> Dodaj parę
          </Button>
        </div>
        {value.image_pairs.length === 0 ? (
          <p className="rounded-md bg-surface p-4 text-center text-xs text-text-muted ring-1 ring-border">
            Brak par zdjęć. Realizacja może istnieć bez zdjęć, ale para „przed/po" robi największe wrażenie.
          </p>
        ) : (
          <ul role="list" className="space-y-3">
            {value.image_pairs.map((pair, i) => (
              <li
                key={i}
                className="rounded-md bg-surface p-3 ring-1 ring-border"
              >
                <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
                  <PairSlot
                    label="Przed"
                    url={pair.before_url}
                    alt={pair.before_alt}
                    onPick={() => setPairPicker({ index: i, side: 'before' })}
                    onAlt={(alt) => onChange({
                      ...value,
                      image_pairs: value.image_pairs.map((p, idx) => idx === i ? { ...p, before_alt: alt || undefined } : p),
                    })}
                  />
                  <PairSlot
                    label="Po"
                    url={pair.after_url}
                    alt={pair.after_alt}
                    onPick={() => setPairPicker({ index: i, side: 'after' })}
                    onAlt={(alt) => onChange({
                      ...value,
                      image_pairs: value.image_pairs.map((p, idx) => idx === i ? { ...p, after_alt: alt || undefined } : p),
                    })}
                  />
                  <div className="flex items-start justify-end">
                    <Button type="button" size="sm" variant="ghost" onClick={() => removePair(i)}>
                      <Trash2 className="size-3.5" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
                <Input
                  className="mt-3"
                  value={pair.caption ?? ''}
                  onChange={(e) => onChange({
                    ...value,
                    image_pairs: value.image_pairs.map((p, idx) => idx === i ? { ...p, caption: e.currentTarget.value || undefined } : p),
                  })}
                  placeholder={'Opis (opcjonalnie): „6 tygodni po zabiegu — pełna remisja zmian"'}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {coverOpen ? (
        <MediaPicker
          title="Wybierz zdjęcie tytułowe"
          onSelect={onPickCover}
          onClose={() => setCoverOpen(false)}
          onClear={() => { onChange({ ...value, cover_url: undefined, cover_alt: undefined }); setCoverOpen(false); }}
        />
      ) : null}

      {pairPicker ? (
        <MediaPicker
          title={`Wybierz zdjęcie „${pairPicker.side === 'before' ? 'przed' : 'po'}"`}
          onSelect={onPickPair}
          onClose={() => setPairPicker(null)}
        />
      ) : null}
    </div>
  );
}

function PairSlot({
  label,
  url,
  alt,
  onPick,
  onAlt,
}: {
  label: string;
  url: string;
  alt?: string;
  onPick: () => void;
  onAlt: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">{label}</p>
      {url ? (
        <button
          type="button"
          onClick={onPick}
          className="block aspect-[4/3] w-full overflow-hidden rounded-md ring-1 ring-border transition-shadow hover:shadow"
        >
          <img src={url} alt={alt ?? ''} className="size-full object-cover" />
        </button>
      ) : (
        <button
          type="button"
          onClick={onPick}
          className="flex aspect-[4/3] w-full items-center justify-center rounded-md bg-bg text-xs text-text-muted ring-1 ring-border ring-dashed transition-colors hover:bg-surface"
        >
          <span className="inline-flex items-center gap-1.5">
            <ImageIcon className="size-4" aria-hidden="true" /> Wybierz zdjęcie
          </span>
        </button>
      )}
      <Input
        value={alt ?? ''}
        onChange={(e) => onAlt(e.currentTarget.value)}
        placeholder="Opis alternatywny (alt)"
      />
    </div>
  );
}
